import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { 
  RefreshCw, CheckCircle2, AlertTriangle, XCircle, 
  Users, Briefcase, ExternalLink, Activity, 
  BarChart3, Clock, Database, Shield
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * BP Status Parity Diagnostics Panel
 * Admin UI for monitoring Builder Prime status parity
 * Shows: sync health, status distribution, unmapped statuses
 */
export const BPStatusDiagnosticsPanel = () => {
  const [loading, setLoading] = useState(true);
  const [syncHealth, setSyncHealth] = useState(null);
  const [leadDistribution, setLeadDistribution] = useState(null);
  const [projectDistribution, setProjectDistribution] = useState(null);
  const [unmappedStatuses, setUnmappedStatuses] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [healthRes, leadRes, projectRes, unmappedRes] = await Promise.all([
        axios.get(`${API}/admin/bp-diagnostics/sync-health`),
        axios.get(`${API}/admin/bp-diagnostics/status-distribution?record_type=lead`),
        axios.get(`${API}/admin/bp-diagnostics/status-distribution?record_type=project`),
        axios.get(`${API}/admin/bp-diagnostics/unmapped-statuses`)
      ]);
      
      setSyncHealth(healthRes.data);
      setLeadDistribution(leadRes.data);
      setProjectDistribution(projectRes.data);
      setUnmappedStatuses(unmappedRes.data);
    } catch (error) {
      console.error('Error fetching BP diagnostics:', error);
      toast.error('Failed to fetch BP diagnostics data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  const hasUnmappedStatuses = unmappedStatuses && 
    ((unmappedStatuses.leads?.length > 0) || (unmappedStatuses.projects?.length > 0));

  const totalUnmapped = unmappedStatuses 
    ? (unmappedStatuses.leads?.reduce((sum, s) => sum + s.count, 0) || 0) + 
      (unmappedStatuses.projects?.reduce((sum, s) => sum + s.count, 0) || 0)
    : 0;

  if (loading) {
    return (
      <Card className="bg-slate-900 border-slate-700">
        <CardContent className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
          <span className="ml-3 text-slate-400">Loading BP Status Diagnostics...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="bp-status-diagnostics">
      {/* Unmapped Status Alert Banner */}
      {hasUnmappedStatuses && (
        <Alert variant="warning" className="bg-amber-900/20 border-amber-600/50" data-testid="unmapped-status-alert">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          <AlertTitle className="text-amber-400 font-semibold">
            Unmapped Statuses Detected ({totalUnmapped} records)
          </AlertTitle>
          <AlertDescription className="text-amber-300/80">
            Some Builder Prime statuses are not recognized in Rhino-1. These are preserved verbatim but need review.
            <Button 
              variant="link" 
              className="text-amber-400 p-0 ml-2 h-auto"
              onClick={() => setActiveTab('unmapped')}
            >
              View Details →
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Header with Refresh */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-500" />
            Builder Prime Status Parity
          </h2>
          <p className="text-sm text-slate-400 mt-1">
            Monitor status mapping health between Builder Prime and Rhino-1
          </p>
        </div>
        <Button 
          variant="outline" 
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing}
          data-testid="refresh-diagnostics-btn"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Leads Health */}
        <Card className="bg-slate-800 border-slate-700" data-testid="leads-health-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-400 flex items-center gap-2">
              <Users className="h-4 w-4" />
              Leads Status Parity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-2">
              <span className="text-3xl font-bold text-white">
                {syncHealth?.leads?.mapping_success_rate?.toFixed(1) || 0}%
              </span>
              {syncHealth?.leads?.mapping_success_rate >= 99 ? (
                <CheckCircle2 className="h-8 w-8 text-green-500" />
              ) : syncHealth?.leads?.mapping_success_rate >= 90 ? (
                <AlertTriangle className="h-8 w-8 text-amber-500" />
              ) : (
                <XCircle className="h-8 w-8 text-red-500" />
              )}
            </div>
            <Progress 
              value={syncHealth?.leads?.mapping_success_rate || 0} 
              className="h-2 bg-slate-700"
            />
            <div className="flex justify-between mt-2 text-xs text-slate-400">
              <span>{(syncHealth?.leads?.total_count || 0).toLocaleString()} total</span>
              <span>{(syncHealth?.leads?.unmapped_count || 0).toLocaleString()} unmapped</span>
            </div>
          </CardContent>
        </Card>

        {/* Projects Health */}
        <Card className="bg-slate-800 border-slate-700" data-testid="projects-health-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-400 flex items-center gap-2">
              <Briefcase className="h-4 w-4" />
              Projects Status Parity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-2">
              <span className="text-3xl font-bold text-white">
                {syncHealth?.projects?.mapping_success_rate?.toFixed(1) || 0}%
              </span>
              {syncHealth?.projects?.mapping_success_rate >= 99 ? (
                <CheckCircle2 className="h-8 w-8 text-green-500" />
              ) : syncHealth?.projects?.mapping_success_rate >= 90 ? (
                <AlertTriangle className="h-8 w-8 text-amber-500" />
              ) : (
                <XCircle className="h-8 w-8 text-red-500" />
              )}
            </div>
            <Progress 
              value={syncHealth?.projects?.mapping_success_rate || 0} 
              className="h-2 bg-slate-700"
            />
            <div className="flex justify-between mt-2 text-xs text-slate-400">
              <span>{(syncHealth?.projects?.total_count || 0).toLocaleString()} total</span>
              <span>{(syncHealth?.projects?.unmapped_count || 0).toLocaleString()} unmapped</span>
            </div>
          </CardContent>
        </Card>

        {/* Overall Health */}
        <Card className="bg-slate-800 border-slate-700" data-testid="overall-health-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-400 flex items-center gap-2">
              <Activity className="h-4 w-4" />
              System Health
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3 mb-3">
              {syncHealth?.health_status === 'healthy' ? (
                <>
                  <Badge className="bg-green-600 text-white">HEALTHY</Badge>
                  <CheckCircle2 className="h-6 w-6 text-green-500" />
                </>
              ) : syncHealth?.health_status === 'warning' ? (
                <>
                  <Badge className="bg-amber-600 text-white">WARNING</Badge>
                  <AlertTriangle className="h-6 w-6 text-amber-500" />
                </>
              ) : (
                <>
                  <Badge className="bg-red-600 text-white">CRITICAL</Badge>
                  <XCircle className="h-6 w-6 text-red-500" />
                </>
              )}
            </div>
            <div className="text-xs text-slate-400 space-y-1">
              <div className="flex items-center gap-2">
                <Clock className="h-3 w-3" />
                Last sync: {syncHealth?.leads?.last_sync 
                  ? new Date(syncHealth.leads.last_sync).toLocaleString() 
                  : 'Never'}
              </div>
              <div className="flex items-center gap-2">
                <Database className="h-3 w-3" />
                Generated: {syncHealth?.generated_at 
                  ? new Date(syncHealth.generated_at).toLocaleTimeString() 
                  : '-'}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 bg-slate-800">
          <TabsTrigger value="overview" className="data-[state=active]:bg-slate-700">
            <BarChart3 className="h-4 w-4 mr-2" />
            Distribution
          </TabsTrigger>
          <TabsTrigger value="unmapped" className="data-[state=active]:bg-slate-700 relative">
            <AlertTriangle className="h-4 w-4 mr-2" />
            Unmapped
            {hasUnmappedStatuses && (
              <Badge className="ml-2 bg-amber-600 text-white text-xs px-1.5">
                {totalUnmapped}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="expected" className="data-[state=active]:bg-slate-700">
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Expected Statuses
          </TabsTrigger>
        </TabsList>

        {/* Distribution Tab */}
        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Lead Status Distribution */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <Users className="h-5 w-5 text-blue-500" />
                  Lead Status Distribution
                </CardTitle>
                <CardDescription>
                  {leadDistribution?.total_records?.toLocaleString() || 0} total leads
                </CardDescription>
              </CardHeader>
              <CardContent className="max-h-96 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700">
                      <TableHead className="text-slate-400">Status</TableHead>
                      <TableHead className="text-slate-400 text-right">Count</TableHead>
                      <TableHead className="text-slate-400 text-center">Mapped</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leadDistribution?.actual_distribution
                      ?.filter(s => s.bp_status)
                      ?.sort((a, b) => b.count - a.count)
                      ?.map((status, idx) => (
                        <TableRow key={idx} className="border-slate-700">
                          <TableCell className="font-medium text-white text-sm">
                            {status.bp_status}
                          </TableCell>
                          <TableCell className="text-right text-slate-300">
                            {status.count.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-center">
                            {status.is_mapped ? (
                              <CheckCircle2 className="h-4 w-4 text-green-500 mx-auto" />
                            ) : (
                              <XCircle className="h-4 w-4 text-red-500 mx-auto" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Project Status Distribution */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <Briefcase className="h-5 w-5 text-green-500" />
                  Project Status Distribution
                </CardTitle>
                <CardDescription>
                  {projectDistribution?.total_records?.toLocaleString() || 0} total projects
                </CardDescription>
              </CardHeader>
              <CardContent className="max-h-96 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700">
                      <TableHead className="text-slate-400">Status</TableHead>
                      <TableHead className="text-slate-400 text-right">Count</TableHead>
                      <TableHead className="text-slate-400 text-center">Mapped</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {projectDistribution?.actual_distribution
                      ?.filter(s => s.bp_status)
                      ?.sort((a, b) => b.count - a.count)
                      ?.map((status, idx) => (
                        <TableRow key={idx} className="border-slate-700">
                          <TableCell className="font-medium text-white text-sm">
                            {status.bp_status}
                          </TableCell>
                          <TableCell className="text-right text-slate-300">
                            {status.count.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-center">
                            {status.is_mapped ? (
                              <CheckCircle2 className="h-4 w-4 text-green-500 mx-auto" />
                            ) : (
                              <XCircle className="h-4 w-4 text-red-500 mx-auto" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Unmapped Statuses Tab */}
        <TabsContent value="unmapped" className="mt-4" data-testid="unmapped-statuses-tab">
          {!hasUnmappedStatuses ? (
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <CheckCircle2 className="h-16 w-16 text-green-500 mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">All Statuses Mapped!</h3>
                <p className="text-slate-400 text-center max-w-md">
                  All Builder Prime statuses are recognized and mapped correctly in Rhino-1.
                  No action required.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {/* Unmapped Lead Statuses */}
              {unmappedStatuses?.leads?.length > 0 && (
                <Card className="bg-slate-800 border-slate-700 border-l-4 border-l-amber-500">
                  <CardHeader>
                    <CardTitle className="text-lg text-white flex items-center gap-2">
                      <Users className="h-5 w-5 text-amber-500" />
                      Unmapped Lead Statuses
                      <Badge variant="outline" className="ml-2 border-amber-500 text-amber-400">
                        {unmappedStatuses.leads.reduce((sum, s) => sum + s.count, 0)} records
                      </Badge>
                    </CardTitle>
                    <CardDescription>
                      These Builder Prime lead statuses are not recognized. They are preserved as-is but should be reviewed.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow className="border-slate-700">
                          <TableHead className="text-slate-400">BP Status (Verbatim)</TableHead>
                          <TableHead className="text-slate-400 text-right">Count</TableHead>
                          <TableHead className="text-slate-400">Sample Records</TableHead>
                          <TableHead className="text-slate-400">First Seen</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {unmappedStatuses.leads.map((status, idx) => (
                          <TableRow key={idx} className="border-slate-700">
                            <TableCell className="font-mono text-amber-400 text-sm">
                              "{status.bp_status || '(empty)'}"
                            </TableCell>
                            <TableCell className="text-right text-white font-semibold">
                              {status.count.toLocaleString()}
                            </TableCell>
                            <TableCell className="text-slate-300">
                              <div className="flex flex-wrap gap-1">
                                {status.sample_ids?.slice(0, 3).map((id, i) => (
                                  <Button 
                                    key={i}
                                    variant="ghost" 
                                    size="sm"
                                    className="h-6 px-2 text-xs text-blue-400 hover:text-blue-300"
                                    onClick={() => window.open(`/leads/${id}`, '_blank')}
                                  >
                                    {id.slice(0, 8)}...
                                    <ExternalLink className="h-3 w-3 ml-1" />
                                  </Button>
                                ))}
                              </div>
                            </TableCell>
                            <TableCell className="text-slate-400 text-sm">
                              {status.first_seen 
                                ? new Date(status.first_seen).toLocaleDateString() 
                                : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* Unmapped Project Statuses */}
              {unmappedStatuses?.projects?.length > 0 && (
                <Card className="bg-slate-800 border-slate-700 border-l-4 border-l-amber-500">
                  <CardHeader>
                    <CardTitle className="text-lg text-white flex items-center gap-2">
                      <Briefcase className="h-5 w-5 text-amber-500" />
                      Unmapped Project Statuses
                      <Badge variant="outline" className="ml-2 border-amber-500 text-amber-400">
                        {unmappedStatuses.projects.reduce((sum, s) => sum + s.count, 0)} records
                      </Badge>
                    </CardTitle>
                    <CardDescription>
                      These Builder Prime project statuses are not recognized. They are preserved as-is but should be reviewed.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow className="border-slate-700">
                          <TableHead className="text-slate-400">BP Status (Verbatim)</TableHead>
                          <TableHead className="text-slate-400 text-right">Count</TableHead>
                          <TableHead className="text-slate-400">Sample Records</TableHead>
                          <TableHead className="text-slate-400">First Seen</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {unmappedStatuses.projects.map((status, idx) => (
                          <TableRow key={idx} className="border-slate-700">
                            <TableCell className="font-mono text-amber-400 text-sm">
                              "{status.bp_status || '(empty)'}"
                            </TableCell>
                            <TableCell className="text-right text-white font-semibold">
                              {status.count.toLocaleString()}
                            </TableCell>
                            <TableCell className="text-slate-300">
                              <div className="flex flex-wrap gap-1">
                                {status.sample_ids?.slice(0, 3).map((id, i) => (
                                  <Button 
                                    key={i}
                                    variant="ghost" 
                                    size="sm"
                                    className="h-6 px-2 text-xs text-blue-400 hover:text-blue-300"
                                    onClick={() => window.open(`/jobs/${id}`, '_blank')}
                                  >
                                    {id.slice(0, 8)}...
                                    <ExternalLink className="h-3 w-3 ml-1" />
                                  </Button>
                                ))}
                              </div>
                            </TableCell>
                            <TableCell className="text-slate-400 text-sm">
                              {status.first_seen 
                                ? new Date(status.first_seen).toLocaleDateString() 
                                : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* Action Guidance */}
              <Alert className="bg-slate-800 border-slate-600">
                <AlertTriangle className="h-4 w-4 text-blue-400" />
                <AlertTitle className="text-blue-400">What to do with unmapped statuses?</AlertTitle>
                <AlertDescription className="text-slate-300">
                  <ul className="list-disc pl-4 mt-2 space-y-1 text-sm">
                    <li>Review if these are new Builder Prime statuses that need to be added to the mapping</li>
                    <li>Check if they are typos or variations of existing statuses</li>
                    <li>Records are preserved with their original BP status - no data is lost</li>
                    <li>Contact the development team to add new status mappings if needed</li>
                  </ul>
                </AlertDescription>
              </Alert>
            </div>
          )}
        </TabsContent>

        {/* Expected Statuses Tab */}
        <TabsContent value="expected" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Expected Lead Statuses */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <Users className="h-5 w-5 text-blue-500" />
                  Expected Lead Statuses (33)
                </CardTitle>
                <CardDescription>
                  Builder Prime lead statuses recognized by Rhino-1
                </CardDescription>
              </CardHeader>
              <CardContent className="max-h-96 overflow-y-auto">
                <div className="space-y-3">
                  {leadDistribution?.expected_statuses && Object.entries(
                    leadDistribution.expected_statuses.reduce((acc, status) => {
                      // Group by category
                      const category = status.startsWith('DEAD') ? 'Dead' :
                        status.startsWith('Reset') ? 'Reset' :
                        ['Job Sold', 'Credit Reject', 'Job Sold & Cancelled', 'Job In Progress', 'Customer'].includes(status) ? 'Sales' :
                        'Pipeline';
                      if (!acc[category]) acc[category] = [];
                      acc[category].push(status);
                      return acc;
                    }, {})
                  ).map(([category, statuses]) => (
                    <div key={category}>
                      <h4 className="text-sm font-semibold text-slate-400 mb-2">{category}</h4>
                      <div className="flex flex-wrap gap-1">
                        {statuses.map((status, idx) => (
                          <Badge 
                            key={idx} 
                            variant="outline" 
                            className="text-xs border-slate-600 text-slate-300"
                          >
                            {status}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Expected Project Statuses */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <Briefcase className="h-5 w-5 text-green-500" />
                  Expected Project Statuses (14)
                </CardTitle>
                <CardDescription>
                  Builder Prime project statuses recognized by Rhino-1
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {projectDistribution?.expected_statuses?.map((status, idx) => (
                    <Badge 
                      key={idx} 
                      variant="outline" 
                      className="text-sm border-slate-600 text-slate-300 py-1"
                    >
                      {status}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BPStatusDiagnosticsPanel;
